import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, Role } from '@/store/auth';
import { useEffect, useState } from 'react';
import { userService } from '@/services/user-service';
import { Loader2 } from 'lucide-react';
import { AccessDenied } from './AccessDenied';

interface AuthGuardProps {
  requiredPermission?: string;
  adminOnly?: boolean;
}

export function AuthGuard({ requiredPermission, adminOnly = false }: AuthGuardProps) {
  const { currentUser, authorizedModules, setAuthorizedModules } = useAuthStore();
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (currentUser && currentUser.role !== Role.ADMIN && authorizedModules.length === 0 && !hasSynced) {
      const syncAccess = async () => {
        setIsSyncing(true);
        try {
          const res = await userService.getUserModules();
          if (res.success) {
            // Map Backend Module Names to Local Slugs
            const moduleMap: Record<string, string> = {
              'BBPS_APP_DEMO': 'bbps-ecosystem',
              'BBPS_BACKEND_DEMO': 'bbps-ecosystem',
              'BBPS_DASHBOARD_DEMO': 'bbps-ecosystem',
              'BBPS_RECON_DEMO': 'bbps-ecosystem',
              'BBPS DIGITAL ECOSYSTEM': 'bbps-ecosystem',
              'MERCHANT_ONBOARDING_DEMO': 'merchant-onboarding',
              'CBS_MOBILE_DEMO': 'cbs-mobile',
              'BANKING_APP_DEMO': 'banking-app',
              'BANK_ONBOARDING_DEMO': 'bank-demo',
              'CBS_ONBOARDING_DEMO': 'cbs-demo',
              'xpressline': 'xpresslinc',
              'BANL_DEMO': 'banl-demo',
              'IMPS_DEMO': 'imps-demo',
              'UPI_SWITCH_DEMO': 'upi-switch-demo',
              'AEPS_SWITCH_DEMO': 'aeps-switch-demo',
              'EKYC_SETU_DEMO': 'aeps-switch-demo',
              'xpresslinc': 'xpresslinc',
              'LOS_LMS_DEMO': 'los-lms',
              'los-lms': 'los-lms'
            };
            const slugs = res.data.filter((m: any) => m.active).map((m: any) => moduleMap[m.moduleName]).filter(Boolean);
            setAuthorizedModules(slugs);
          }
        } catch (error) {
          console.error('Core gateway sync protocol fail', error);
        } finally {
          setIsSyncing(false);
          setHasSynced(true);
        }
      };
      syncAccess();
    }
  }, [currentUser, authorizedModules.length, setAuthorizedModules]);

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only allow navigation after sync if it's required
  const needsSync = currentUser && currentUser.role !== Role.ADMIN && authorizedModules.length === 0 && !hasSynced;

  if (isSyncing || (needsSync && !isSyncing)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#f8f9fa]">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 animate-pulse">Syncing Sentinel Gateways...</span>
      </div>
    );
  }

  // Admin Protections
  if (adminOnly && currentUser.role !== Role.ADMIN) {
    return <AccessDenied adminOnly />;
  }

  // Permission Check
  if (requiredPermission && currentUser.role !== Role.ADMIN) {
    const isAuthorized = authorizedModules.some(module => {
      // BBPS Global Exception: ecosystem access grants all sub-module access
      if (module === 'bbps-ecosystem' && requiredPermission.startsWith('bbps-')) return true;
      // Bharat Connect shares the BBPS privilege — BBPS access grants all Bharat Connect access
      if (module === 'bbps-ecosystem' && requiredPermission.startsWith('bharat-connect')) return true;
      // Bharat Connect Global Exception: top-level access grants all sub-module access
      if (module === 'bharat-connect' && requiredPermission.startsWith('bharat-connect')) return true;
      return module === requiredPermission;
    });

    if (!isAuthorized) {
      return <AccessDenied requiredPermission={requiredPermission} />;
    }
  }

  return <Outlet />;
}
